import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AppThunk } from '../store'

import {
  getMonthlyWorkingTime,
  getMonthlyUserData,
  getMonthlyLineChart,
  getMonthlyReportExists,
  getMonthlyHeatMap,
  getMonthlyWorkDetail,
  getMonthlyTimeLine,
} from '../../apis/teamReport'

import {
  getReportTeam,
} from '../../apis/team'

import {
  TeamCondition,
  MonthlySearchCondition,
  Team,
  TeamReportWorkingTime,
  TeamReportLineChart,
  TeamReportWorkDetail,
  TeamReportHeatMap,
  TeamReportTimeLine,
} from '../../types/team'
import { setErrors } from '../common/errorsSlice'

import {
  UserData,
  DataExists,
} from '../../types/report'

const initialState: Partial<ReportState> = {
  evaluationExists: undefined,
  userProcessExists: undefined,
  isTeamLoading: true,
  isWorkingTimeLoading: true,
  isUserDataLoading: true,
  isLineChartDataLoading: true,
  isWorkDetailLoading: true,
  isHeatMapLoading: true,
  isTimeLineLoading: true,
  isDisApprove: undefined,
  isDataExistsLoading: true,
}

interface ReportState {
  evaluationExists: boolean
  userProcessExists: boolean
  team: Team
  workingTime: TeamReportWorkingTime
  userData: UserData[]
  lineChartData: TeamReportLineChart
  workDetail: TeamReportWorkDetail
  heatMap: TeamReportHeatMap
  timeLine: TeamReportTimeLine[]
  isTeamLoading: boolean
  isWorkingTimeLoading: boolean
  isUserDataLoading: boolean
  isLineChartDataLoading: boolean
  isWorkDetailLoading: boolean
  isHeatMapLoading: boolean
  isTimeLineLoading: boolean
  isDisApprove: boolean | undefined
  isDataExistsLoading: boolean,
}

const monthlyTeamReportSlice = createSlice({
  name: 'monthlyTeamReport',
  initialState,
  reducers: {
    requestTeamStart: (state) => {
      state.isTeamLoading = true
      return state
    },
    requestWorkingTimeStart: (state) => {
      state.isWorkingTimeLoading = true
      return state
    },
    requestUserDataStart: (state) => {
      state.isUserDataLoading = true
      return state
    },
    requestLineChartDataStart: (state) => {
      state.isLineChartDataLoading = true
      return state
    },
    requestWorkDetailStart: (state) => {
      state.isWorkDetailLoading = true
      return state
    },
    requestHeatMapStart: (state) => {
      state.isHeatMapLoading = true
      return state
    },
    requestTimeLineStart: (state) => {
      state.isTimeLineLoading = true
      return state
    },
    requestDataExistsStart: (state) => {
      state.isDataExistsLoading = true
      return state
    },
    buildTeamReportDataExists(state, action: PayloadAction<DataExists>) {
      state.isDataExistsLoading = false
      state.evaluationExists = action.payload.evaluation_exists
      state.userProcessExists = action.payload.user_process_exists
      return state
    },
    buildTeamSuccess(state, action: PayloadAction<Team>) {
      state.isTeamLoading = false
      state.team = action.payload
      return state
    },
    buildTeamReportWorkingTimeSuccess: (state, action: PayloadAction<TeamReportWorkingTime>) => {
      state.isWorkingTimeLoading = false
      state.workingTime = action.payload
      return state
    },
    buildTeamReportUserDataSuccess: (state, action: PayloadAction<UserData[]>) => {
      state.isUserDataLoading = false
      state.userData = action.payload
      return state
    },
    buildTeamReportLineChartDataSuccess: (state, action: PayloadAction<TeamReportLineChart>) => {
      state.isLineChartDataLoading = false
      state.lineChartData = action.payload
      return state
    },
    buildTeamReportWorkDetailSuccess: (state, action: PayloadAction<TeamReportWorkDetail>) => {
      state.isWorkDetailLoading = false
      state.workDetail = action.payload
      return state
    },
    buildTeamReportHeatMapSuccess: (state, action: PayloadAction<TeamReportHeatMap>) => {
      state.isHeatMapLoading = false
      state.heatMap = action.payload
      return state
    },
    buildTeamReportTimeLineSuccess: (state, action: PayloadAction<TeamReportTimeLine[]>) => {
      state.isTimeLineLoading = false
      state.timeLine = action.payload
      return state
    },
    setMemberReportApproval: (state) => {
      state.isDisApprove = false
      return state
    },
    setMemberReportDisApproval: (state) => {
      state.evaluationExists = false
      state.userProcessExists = false
      state.isDisApprove = true
      return state
    },
    setTeamReportApproval: (state) => {
      state.isDataExistsLoading = false
      state.isDisApprove = false
      return state
    },
    setTeamReportDisApproval: (state) => {
      state.isTeamLoading = false
      state.isWorkingTimeLoading = false
      state.isUserDataLoading = false
      state.isLineChartDataLoading = false
      state.isWorkDetailLoading = false
      state.isHeatMapLoading = false
      state.isTimeLineLoading = false
      state.evaluationExists = false
      state.userProcessExists = false
      state.isDisApprove = true
      return state
    },
    requestFailure: (state) => state,
    resetState: (state) => {
      // eslint-disable-next-line no-param-reassign
      state = initialState
      return state
    },
  },
})

export const {
  requestTeamStart,
  requestWorkingTimeStart,
  requestUserDataStart,
  requestLineChartDataStart,
  requestWorkDetailStart,
  requestHeatMapStart,
  requestTimeLineStart,
  requestDataExistsStart,
  buildTeamSuccess,
  buildTeamReportWorkingTimeSuccess,
  buildTeamReportUserDataSuccess,
  buildTeamReportLineChartDataSuccess,
  buildTeamReportWorkDetailSuccess,
  buildTeamReportDataExists,
  buildTeamReportHeatMapSuccess,
  buildTeamReportTimeLineSuccess,
  requestFailure,
  setTeamReportApproval,
  setTeamReportDisApproval,
  resetState,
} = monthlyTeamReportSlice.actions

export const monthlyTeamReportReducer = monthlyTeamReportSlice.reducer

export const fetchReportWorkingTime = (
  param: MonthlySearchCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestWorkingTimeStart())
  getMonthlyWorkingTime(param)
    .then((result: TeamReportWorkingTime) => {
      dispatch(buildTeamReportWorkingTimeSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchReportUserData = (
  param: MonthlySearchCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestWorkingTimeStart())
  getMonthlyUserData(param)
    .then((result: UserData[]) => {
      dispatch(buildTeamReportUserDataSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchReportLineChart = (
  param: MonthlySearchCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestLineChartDataStart())
  getMonthlyLineChart(param)
    .then((result: TeamReportLineChart) => {
      dispatch(buildTeamReportLineChartDataSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchReportWorkDetail = (
  param: MonthlySearchCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestWorkingTimeStart())
  getMonthlyWorkDetail(param)
    .then((result: TeamReportWorkDetail) => {
      dispatch(buildTeamReportWorkDetailSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchReportHeatMap = (
  param: MonthlySearchCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestHeatMapStart())
  getMonthlyHeatMap(param)
    .then((result: TeamReportHeatMap) => {
      dispatch(buildTeamReportHeatMapSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchReportTimeLine = (
  param: MonthlySearchCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestTimeLineStart())
  getMonthlyTimeLine(param)
    .then((result: TeamReportTimeLine[]) => {
      dispatch(buildTeamReportTimeLineSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchReportTeam = (
  param: TeamCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestTeamStart())
  getReportTeam(param)
    .then((result: Team) => {
      dispatch(buildTeamSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchTeamReportExists = (
  param: MonthlySearchCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestDataExistsStart())
  getMonthlyReportExists(param)
    .then((result: DataExists) => {
      dispatch(setTeamReportApproval())
      dispatch(buildTeamReportDataExists(result))
    })
    .catch((error) => {
      switch (error.status) {
        case 403:
          dispatch(setTeamReportDisApproval())
          break
        case 409:
          dispatch(setTeamReportDisApproval())
          break
        default:
          dispatch(setErrors(error))
          break
      }
    })
}
